/** Pure helpers for the Weekly panel: chips, who may do what, and which pieces the pickers offer. */
import type { PackageSlot, PieceRow, UsedPiece, WeeklyPackage } from '../api'

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'plain'
const TZ = 'America/New_York'

/** The package's chip. A planned or complete package follows the live answer (`state`) when the server gives it:
 *  a piece can be reopened after the status was last written. */
export function packageChip(pkg: Pick<WeeklyPackage, 'status' | 'state'>, on = true): { label: string; tone: Tone } {
  switch (pkg.status) {
    case 'planned':
    case 'complete': {
      const ready = pkg.state ? pkg.state === 'complete' : pkg.status === 'complete'
      if (ready && !on) return { label: 'Ready (switched off)', tone: 'plain' }
      return ready ? { label: 'Ready', tone: 'good' } : { label: 'Incomplete', tone: 'warn' }
    }
    case 'sending': return { label: 'Sending', tone: 'info' }
    case 'held': return { label: 'Held', tone: 'bad' }
    case 'sent': return { label: 'Sent', tone: 'good' }
    case 'staff_sent': return { label: 'Sent to staff', tone: 'info' }
    case 'not_sent': return { label: 'Not sent', tone: 'bad' }
    case 'cancelled': return { label: 'Cancelled', tone: 'plain' }
  }
}

export const TONE_CLASS: Record<Tone, string> = {
  good: 'bg-radar-green/15 text-radar-green',
  warn: 'bg-amber-wash text-amber-deep',
  bad: 'bg-radar-red/15 text-radar-red',
  info: 'bg-radar-cyan/15 text-radar-cyan',
  plain: 'bg-ink-600 text-paper-dim',
}

const etDay = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: TZ })

/** True when `iso` falls on today's date in Eastern time. */
export function isSendDay(sendAtIso: string, now: Date): boolean {
  return etDay(new Date(sendAtIso)) === etDay(now)
}

/** Release: a held package, on its send day, for someone who can publish. The server decides the rest. */
export function canRelease(pkg: Pick<WeeklyPackage, 'status' | 'send_at'>, now: Date, editor: boolean): boolean {
  return editor && pkg.status === 'held' && isSendDay(pkg.send_at, now)
}

/** Pick, change and cancel: editors only, and only while the package is still open to change. */
export function canChange(pkg: Pick<WeeklyPackage, 'status'> | null, editor: boolean): boolean {
  return editor && (pkg === null || pkg.status === 'planned' || pkg.status === 'complete' || pkg.status === 'held')
}

/** The server's prefix for a week whose emails can't be built (weekly/tick.py ``_problem``). */
const CANT_BE_MADE = "The emails can't be made: "

/** Held and planned packages say why in plain words (the server's text). A planned package whose
 *  pieces already warn about their gaps does not say it twice. */
export function holdNote(pkg: Pick<WeeklyPackage, 'status' | 'hold_reason'> & Partial<Pick<WeeklyPackage, 'full' | 'short'>>): string | null {
  if (pkg.status === 'planned' && pkg.hold_reason?.startsWith(CANT_BE_MADE)) {
    const shown = [...(pkg.full?.gaps ?? []), ...(pkg.short?.gaps ?? [])]
    const listed = pkg.hold_reason.slice(CANT_BE_MADE.length).replace(/\.$/, '').split('; ')
    if (listed.every((g) => shown.includes(g))) return null
  }
  return (pkg.status === 'held' || pkg.status === 'planned') && pkg.hold_reason ? pkg.hold_reason : null
}

/** A picked piece's gaps (the server's words) as one warning, with how to fix a missing deck:
 *  shown at booking, so it is not first heard of as a hold at 10:00. */
export function gapWarning(gaps: string[] | undefined): string | null {
  if (!gaps?.length) return null
  const fix = gaps.some((g) => g.endsWith('has no deck'))
    ? ' A subtitle (H3) at the top of the piece, or its search description, becomes the email’s deck.' : ''
  return `The email can't be made until this is fixed: ${gaps.join('; ')}.${fix}`
}

/** The upcoming list never shows a cancelled package; its date reads as empty. */
export function visibleUpcoming(slots: PackageSlot[]): PackageSlot[] {
  return slots.map((s) => (s.package?.status === 'cancelled' ? { ...s, package: null } : s))
}

export type Role = 'full' | 'short'
/** Destination each pick must have: the full article is members-only, the short version is a free-section piece. */
export const ROLE_DESTINATION: Record<Role, 'membership' | 'section'> = { full: 'membership', short: 'section' }

/** One picker line. `used` is set when the piece is already in a package: greyed out unless it is that package's own. */
export type PickerOption = { id: number; title: string; approved: boolean; used?: { packageId: number; note: string } }

/** "used Oct 22" for a package that went out, "in the Oct 29 package" for one still to come. */
export function usedNote(u: Pick<UsedPiece, 'send_at' | 'went'>): string {
  const day = new Date(u.send_at).toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' })
  return u.went ? `used ${day}` : `in the ${day} package`
}

/** The pieces a picker offers: the right destination, in review or approved; approved first.
 *  Pieces already in a package stay listed, after the rest, with the date they were used. */
export function pickerPieces(rows: Pick<PieceRow, 'id' | 'title' | 'destination' | 'stage'>[], role: Role, withLive = false, used: UsedPiece[] = []): PickerOption[] {
  const want = ROLE_DESTINATION[role]
  const live = withLive   // either piece may already be live; the package publishes only the other
  const byId = new Map(used.map((u) => [u.draft_id, u]))
  const rank = (o: PickerOption) => (o.used ? 2 : o.approved ? 0 : 1)
  return rows
    .filter((r) => r.destination === want && (r.stage === 'to_review' || r.stage === 'approved' || (live && r.stage === 'live')))
    .map((r): PickerOption => {
      const u = byId.get(r.id)
      if (u) return { id: r.id, title: r.title, approved: r.stage !== 'to_review', used: { packageId: u.package_id, note: usedNote(u) } }
      return { id: r.id, title: r.stage === 'live' ? `${r.title} (${ALREADY_LIVE})` : r.title, approved: r.stage !== 'to_review' }
    })
    .sort((x, y) => rank(x) - rank(y))
}

/** A used piece can be picked only in its own package. */
export function pickable(o: PickerOption, packageId: number | null): boolean {
  return !o.used || o.used.packageId === packageId
}

export const ALREADY_LIVE = 'already live'

/** 10:00 on 2026-10-22 in Eastern time, as a UTC ISO string. */
export function easternToIso(day: string, time: string): string {
  const [y, mo, d] = day.split('-').map(Number)
  const [h, mi] = time.split(':').map(Number)
  const wall = Date.UTC(y, mo - 1, d, h, mi)
  const offset = (at: number) => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' })
      .formatToParts(new Date(at)).map((x) => [x.type, x.value]))
    return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - at
  }
  let at = wall - offset(wall)
  at = wall - offset(at)
  return new Date(at).toISOString()
}

/** Today's date in Eastern time, YYYY-MM-DD. */
export function easternToday(now: Date): string {
  return now.toLocaleDateString('en-CA', { timeZone: TZ })
}

export const AWAITING_APPROVAL = 'awaiting approval'

/** "Wed Oct 28, 10:00 AM ET" */
export function sendWhen(iso: string): string {
  const d = new Date(iso)
  const day = d.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '')
  const t = d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })
  return `${day}, ${t} ET`
}

/** "1,204 sent, 612 opened, 88 clicked" */
export function numbersText(n: { sent: number; opens: number; clicks: number }): string {
  return `${n.sent.toLocaleString('en-US')} sent, ${n.opens.toLocaleString('en-US')} opened, ${n.clicks.toLocaleString('en-US')} clicked`
}

/** What the panel says about the title's switches; null when the package is on and sending to real readers. */
export function switchBanner(on: boolean, live: boolean): string | null {
  if (!on) return 'Weekly package is switched off for this title \u2014 nothing will publish or send.'
  if (!live) return 'Staff only: both emails go only to staff this week.'
  return null
}

/** What a release did, from the status the server returned. */
export function releaseMessage(status: string): string {
  return status === 'sent' || status === 'staff_sent' ? 'Released \u2014 both emails have been sent.' : 'Released \u2014 it will send when both pieces are ready.'
}
